import { useEffect } from 'react';
import { type ExecEdEligibilityBundle, type ExecEdCertCategory } from '../../ExecEdEligibility.types';
import { useTranslation } from 'react-i18next';
import { ExecEdCertCategoryConfigs } from '../../helpers/ExecEdCategories';
import { programCategoryForProgramType } from '../../helpers/utils';
import { useWindowSize } from 'react-use';
import { getProgramConfigValue, type ProgramType } from 'Program';
import { CertActionButton } from './CertActionButton';

type Props = {
    category: ExecEdCertCategory;
    eligibilityBundle: ExecEdEligibilityBundle;
    onReviewRegisterClick: (programType: ProgramType) => void;
    onLearnMoreClick: (programType: ProgramType) => void;
};

export function CertEligibleProgramCategory({
    category,
    eligibilityBundle,
    onReviewRegisterClick,
    onLearnMoreClick,
}: Props) {
    const { t } = useTranslation('back_royal');
    const categoryConfig = ExecEdCertCategoryConfigs[category];
    const eligibleProgramsToDisplay = eligibilityBundle.eligiblePrograms.filter(
        p =>
            programCategoryForProgramType(p.programType) === category &&
            p.programType !== eligibilityBundle.recommendedProgramType,
    );

    const { width } = useWindowSize();
    const isSmOrWider = width >= 576;

    // Ensure all program title elements with a category are the same height so that the action buttons
    // align within the same row when at a viewport width where we display 2 programs in a row.
    useEffect(() => {
        const getProgramTitleElement = (programType: string) => document.getElementById(`program-title-${programType}`);
        const greatestProgramTitleHeight = eligibleProgramsToDisplay.reduce((acc, program) => {
            const programTitleElement = getProgramTitleElement(program.programType);
            return programTitleElement ? Math.max(acc, programTitleElement.clientHeight) : acc;
        }, 0);
        eligibleProgramsToDisplay.forEach(program => {
            const programTitleElement = getProgramTitleElement(program.programType);
            if (!programTitleElement) return;
            programTitleElement.style.height = isSmOrWider ? `${greatestProgramTitleHeight}px` : 'auto';
        });
    }, [eligibleProgramsToDisplay, isSmOrWider]);

    return (
        <div data-testid="program-category" className="mb-[30px]">
            <div className="mb-[10px] flex items-center justify-center text-xs leading-4 opacity-60">
                {t(categoryConfig.modalHeadingLocale.key, categoryConfig.modalHeadingLocale.values ?? {})}
            </div>
            <div className="mx-auto flex justify-center xs:ms-[130px]">
                <div className="flex w-full flex-wrap justify-center gap-x-[20px] gap-y-[30px] xs:w-[550px] xs:min-w-[550px] xs:justify-start">
                    {eligibleProgramsToDisplay.map(program => {
                        const programTitle = getProgramConfigValue(program.programType, 'standaloneShortProgramTitle');

                        return (
                            <div
                                key={program.programType}
                                data-testid="program"
                                className="flex w-[80%] flex-col items-center gap-[10px] xs:w-[230px] xs:items-start"
                            >
                                <div
                                    id={`program-title-${program.programType}`}
                                    className="flex justify-center text-center text-md leading-[21px] xs:justify-start xs:text-start"
                                >
                                    {programTitle as string}
                                </div>
                                <CertActionButton
                                    className="border-opacity-20"
                                    onClick={() => onReviewRegisterClick(program.programType)}
                                >
                                    {t('exec_ed_eligibility.cert_acceptance_modal.review_and_register')}
                                </CertActionButton>
                                <button
                                    type="button"
                                    className="border-none bg-transparent p-0 text-xxs leading-[14px] underline"
                                    onClick={() => onLearnMoreClick(program.programType)}
                                >
                                    {t('exec_ed_eligibility.cert_acceptance_modal.learn_more')}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
